import { Section } from '@/components/layout/Section';
import { Watermark } from '@/components/brand/Watermark';
import { SplitText } from '@/components/motion/SplitText';
import { Reveal } from '@/components/motion/Reveal';
import { StaggerGroup } from '@/components/motion/StaggerGroup';
import { Button } from '@/components/ui/Button';
import { privacy } from '@/content/privacy';
import { site } from '@/content/site';
import { usePageMeta } from '@/lib/usePageMeta';

export default function Privacy() {
  usePageMeta(
    'Privacy Policy',
    'What Falcon Solutions collects, why, and what we do not do. No tracking, no cookies, no selling your details on.',
  );

  return (
    <>
      {/*
        Deliberately a shorter hero than the marketing pages (min-h-[62svh], not
        88): this is a reference document, so the reason to be here is the text
        below the fold, and a full-height hero would only delay it.
      */}
      <section className="relative flex flex-col overflow-hidden bg-ink pt-36 pb-12 text-white md:pt-40 md:pb-14">

        <div className="shell relative z-10 flex flex-1 flex-col justify-center">
          <div className="max-w-[62ch]">
            <Reveal y={12}>
              <span className="flex items-center gap-4">
                <span className="h-px w-10 bg-accent" />
                <span className="text-eyebrow text-white/60">{privacy.hero.eyebrow}</span>
              </span>
            </Reveal>

            <SplitText
              as="h1"
              text={privacy.hero.headline}
              className="mt-8 max-w-[24ch] text-[2.5rem] leading-[0.98] tracking-[-0.02em] sm:text-[3.2rem] lg:text-[4rem]"
            />

            <Reveal delay={0.3}>
              <p className="mt-8 max-w-[52ch] text-lg leading-relaxed text-white/75 md:text-xl">
                {privacy.hero.subcopy}
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <p className="mt-8 text-xs tracking-widest text-white/65">
                Last updated {privacy.lastUpdated}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Summary strip — the three things most visitors came to check. */}
        <div className="shell relative z-10 pt-12">
          <StaggerGroup
            className="grid gap-8 border-t border-white/15 pt-6 sm:grid-cols-3"
            stagger={0.07}
          >
            {privacy.summary.map((item) => (
              <div key={item.k}>
                <p className="font-display text-lg font-bold text-accent">
                  {item.k}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-white/55">{item.v}</p>
              </div>
            ))}
          </StaggerGroup>
        </div>

      </section>

      {/* Policy body */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          {/*
            Contents rail. Sticky from `lg` up only — below that it sits above
            the text as a plain list, which is more useful on a phone than a
            bar that eats vertical space while you read.
          */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <p className="text-eyebrow opacity-65">Contents</p>
                <nav aria-label="Sections of this policy">
                  <ul className="mt-5 space-y-0.5 border-l border-hairline pl-5">
                    {privacy.sections.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="inline-block py-2 text-sm opacity-65 transition-opacity hover:opacity-100"
                        >
                          {s.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </Reveal>
            </div>
          </aside>

          <div className="min-w-0 lg:col-span-8">
            {privacy.sections.map((section) => (
              <Reveal key={section.id}>
                {/*
                  scroll-mt clears the fixed nav so an anchored heading is not
                  hidden underneath it when you jump from the contents rail.
                */}
                <section
                  id={section.id}
                  className="scroll-mt-32 border-t border-hairline pt-10 first:border-t-0 first:pt-0 [&:not(:first-child)]:mt-12"
                >
                  <h2
                    className="font-display text-2xl font-bold md:text-3xl"
                    style={{ fontStretch: '100%' }}
                  >
                    {section.heading}
                  </h2>

                  {section.body.map((para, i) => (
                    <p key={i} className="mt-5 max-w-[68ch] leading-relaxed opacity-75">
                      <EmailText text={para} />
                    </p>
                  ))}

                  {'list' in section && section.list && (
                    <ListBlock title={section.list.title} items={section.list.items} />
                  )}
                  {'list2' in section && section.list2 && (
                    <ListBlock title={section.list2.title} items={section.list2.items} />
                  )}

                  {'after' in section &&
                    section.after?.map((para, i) => (
                      <p key={i} className="mt-6 max-w-[68ch] leading-relaxed opacity-75">
                        <EmailText text={para} />
                      </p>
                    ))}
                </section>
              </Reveal>
            ))}

            <Reveal>
              <div className="mt-14 rounded-card border border-hairline bg-surface-muted p-7">
                <h2
                  className="font-display text-lg font-bold"
                  style={{ fontStretch: '100%' }}
                >
                  Contact
                </h2>
                <p className="mt-3 max-w-[60ch] text-sm leading-relaxed opacity-75">
                  {site.legalName} — questions about this policy, or a request about information we
                  hold, go to{' '}
                  <a
                    href={`mailto:${site.email.general}`}
                    className="text-brand underline underline-offset-4 transition-opacity hover:opacity-70"
                  >
                    {site.email.general}
                  </a>
                  .
                </p>
                <p className="mt-3 text-sm opacity-60">
                  {site.locations.map((loc) => `${loc.city}, ${loc.country}`).join(' · ')}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section tone="gradient">
        <Watermark position="right" scale={1.5} opacity={0.05} />
        <div className="relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <SplitText
              as="h2"
              text={privacy.cta.heading}
              className="text-display-md max-w-[14ch] leading-[1.08]"
            />
            <Reveal delay={0.25}>
              <p className="text-body-lg mt-5 max-w-[48ch] text-white/70">{privacy.cta.body}</p>
            </Reveal>
          </div>
          <Reveal delay={0.4} className="shrink-0">
            <Button href={privacy.cta.button.href} variant="inverse" size="lg">
              {privacy.cta.button.label}
            </Button>
          </Reveal>
        </div>
      </Section>
    </>
  );
}

function ListBlock({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <div className="mt-7">
      <h3 className="text-eyebrow opacity-65">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-sm leading-relaxed opacity-75">
            <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmailText({ text }: { text: string }) {
  return <>{text.split(/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi).map((part, index) =>
    index % 2 === 1 ? <a key={index} href={`mailto:${part}`} className="text-brand underline underline-offset-4 break-words hover:opacity-70">{part}</a> : part
  )}</>;
}
